import { FC, useEffect, useMemo, useState } from 'react'
import * as Yup from 'yup'
import { Formik } from 'formik'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { setOriginBusinessUnits } from '../../store/slices'
import { useContractDestinationBUs } from '../../hooks/useContractDestinationBUs'
import { resolveDestinationBusinessUnits } from '../../utils/destinationBusinessUnits'
import { createPreShipment, getOriginBusinessUnits } from '../../services'

import {
  AccountInterface,
  BusinessUnitInterface,
  CoordinatesInterface,
  DeliveryEnum,
  ItemInterface,
  PaymentModeId,
  PieceInterface,
  PreShipmentInterface,
  RangeDeclareDto,
  ServiceDTO,
  ShippingMethodEnum,
  ShipmentStatusEnum,
} from '../../interfaces'
import {
  Card,
  MainPage,
  PageTitle,
  SenderForm,
  ConsigneeFormV2,
  OriginForm,
  PresipmentMessageModal,
  ConfirmModalStatus,
  ModalLoading,
  PreShipmentFormV2,
} from '../../components'
import { PackageTypeEnum } from '../../interfaces/Shipment/PackageTypeEnum'
import { WeightTypeEnum } from '../../interfaces/Shipment/WeightTypeEnum'
import { ScrollToError } from '../../components/Atoms/ScrollToFieldError'
import { PresipmentImpresionModal } from '../../components/Preshipment/PreshipmentImpresionModal'
import { ShipmentService } from '../../interfaces/Services/ServicesEnum'
import { resetValuesKeepingOrigin } from '../../utils/resetShipmentForm'

const CreatePreShipment: FC = () => {
  const rangeMinDeclareList = useAppSelector(
    (state) => state.inmutable.rangeDeclareList
  )
  const newPieceInitialValues = {
    category: '',
    height: '',
    width: '',
    length: '',
    weight: '',
    declaredValue: '0',
    amount: 1,
    packageType: '20',
  }

  const validationSchema = Yup.object().shape({
    sender: Yup.object().shape({
      fullName: Yup.string().required('Este campo es requerido'),
      abbreviationName: Yup.object().required('Este campo es requerido'),
      identificationNumber: Yup.string().required('Este campo es requerido'),
    }),
    // [D4] Obliga a seleccionar la oficina origen (con 1 sola ya viene set).
    origin: Yup.object().shape({
      businessUnit: Yup.object().required('Este campo es requerido').nullable(),
    }),
    consignee: Yup.object().shape({
      fullName: Yup.string().required('Este campo es requerido'),
      abbreviationName: Yup.object().required('Este campo es requerido'),
      identificationNumber: Yup.string().required('Este campo es requerido'),
      phoneNumber: Yup.string()
        .required('Este campo es requerido')
        .test({
          name: 'number',
          message: 'Debe tener un maximo de 7 caracteres',
          test: (number, parent) => {
            if (parent.parent.codePhoneNumber !== 'FIJO' && number.length > 7)
              return false

            return true
          },
        })
        .test({
          name: 'number',
          message: 'Debe tener un maximo de 10 caracteres',
          test: (number, parent) => {
            if (parent.parent.codePhoneNumber === 'FIJO' && number.length > 10)
              return false

            return true
          },
        }),
      codePhoneNumber: Yup.string().required('Este campo es requerido'),
      cellPhoneNumber: Yup.string()
        .test({
          name: 'number',
          message: 'Debe tener un maximo de 7 caracteres',
          test: (number, parent) => {
            if (
              parent.parent.codecellNumber !== 'FIJO' &&
              number !== undefined &&
              number?.length > 7
            )
              return false

            return true
          },
        })
        .test({
          name: 'number',
          message: 'Debe tener un maximo de 10 caracteres',
          test: (number, parent) => {
            if (
              parent.parent.codecellNumber === 'FIJO' &&
              number !== undefined &&
              number?.length > 10
            )
              return false

            return true
          },
        }),
      email: Yup.string()
        .email('Correo invalido. Ejemplo: tealca@tealca.com')
        .required('Este campo es requerido'),
      city: Yup.string().required('Este campo es requerido'),
      address: Yup.string().required('Este campo es requerido'),
      businessUnit: Yup.object().required('Este campo es requerido'),
      accountSelected: Yup.object().required('Este campo es requerido'),
      ShipmentType: Yup.string().required('Este campo es requerido'),
    }),
    shipment: Yup.object().shape({
      pieces: Yup.array()
        .test({
          name: 'pieces',
          message: 'Debe agregar al menos una pieza',
          test: (pieces) => (pieces?.length ?? 0) > 0,
        })
        .test({
          name: 'pieces-weight',
          skipAbsent: true,
          test: function (pieces, context) {
            const { serviceData } = context.parent // Asumiendo que guardaste el servicio aquí
            if (!serviceData || !pieces) return true

            const sum = pieces.reduce((acc, piece) => acc + piece.weight, 0)
            const min = serviceData.minWeight ?? 0
            const max = serviceData.maxWeight ?? Infinity

            if (sum < min) {
              return context.createError({
                message: `El peso total debe ser mayor o igual a ${min}kg`,
              })
            }

            if (sum > max) {
              return context.createError({
                message: `El peso total debe ser menor o igual a ${max}kg`,
              })
            }

            // Peso a facturar = mayor entre balanza y volumétrico (lo calcula el
            // backend; ver GetChargedWeight). Puede superar el tope aunque la
            // balanza no lo haga, si las piezas son voluminosas.
            const chargedWeight = context.parent.totalChargedWeight ?? 0
            if (chargedWeight > max) {
              return context.createError({
                message: `El peso a facturar (${chargedWeight.toFixed(
                  2
                )}kg) supera el máximo permitido (${max}kg): disminuya las dimensiones o el peso de las piezas`,
              })
            }

            return true
          },
        }),
      valueDeclarate: Yup.string().when('isSafeKeeping', {
        is: (isSafeKeeping: boolean) => isSafeKeeping,
        then: () =>
          Yup.string()
            .required('Este campo es requerido')
            .test({
              name: 'declared-min-value',
              message: 'El valor declarado debe ser mayor a 0',
              test: (value) => {
                const numericValue = parseFloat(value || '0')
                return numericValue > 0
              },
            })
            .test({
              name: 'declared',
              skipAbsent: true,
              test: (value, parent) => {
                if (
                  (parent.parent.paymentModeID as PaymentModeId)?.toString() ===
                  PaymentModeId.CREDIT?.toString()
                ) {
                  if (
                    (parent.parent.service as ShipmentService)?.toString() ===
                    ShipmentService.DOCUMENT?.toString()
                  ) {
                    const totalChargedWeight =
                      parent.parent?.totalChargedWeight ?? 0

                    let totalValue = 0
                    totalValue = minDeclaredValue(
                      totalChargedWeight ?? 0,
                      rangeMinDeclareList,
                      parent.parent.service
                    )
                    const numericValue = parseFloat(value || '0')

                    if (numericValue < totalValue) {
                      return parent.createError({
                        message: `El valor declarado no puede ser menor a ${totalValue.toFixed(
                          2
                        )}$`,
                      })
                    }
                  } else if (parseFloat(value || '0') >= 1) return true
                  else
                    return parent.createError({
                      message: `El valor declarado no puede ser mayor o igual a 1$`,
                    })
                }

                const totalChargedWeight =
                  parent.parent?.totalChargedWeight ?? 0

                let totalValue = 0
                totalValue = minDeclaredValue(
                  totalChargedWeight ?? 0,
                  rangeMinDeclareList,
                  parent.parent.service
                )
                const numericValue = parseFloat(value || '0')

                if (numericValue < totalValue) {
                  return parent.createError({
                    message: `El valor declarado no puede ser menor a ${totalValue.toFixed(
                      2
                    )}$`,
                  })
                }

                return true
              },
            }),
      }),
      declaratedValueInvoceNum: Yup.string().when(
        ['valueDeclarate', 'isSafeKeeping'],
        {
          is: (valueDeclarate: string, isSafeKeeping: boolean) => {
            const numericValue = parseFloat(valueDeclarate || '0')
            return isSafeKeeping && numericValue >= 1500
          },
          then: () =>
            Yup.string().required(
              'El número de factura es requerido para valores declarados mayores a 1500$'
            ),
        }
      ),
      observations: Yup.string().max(150, 'maximo 150 caracteres'),
      service: Yup.string().required('El servicio es Requerido'),
      // fileUpload: Yup.mixed().test(
      //   'fileValidation',
      //   'Error en archivos',
      //   (value, context) => {
      //     if (!value) return true;

      //     const files = Array.isArray(value) ? value : [value];
      //     const errors: FileError[] = [];

      //     // Validación de cantidad máxima de archivos
      //     if (files.length > 5) {
      //       errors.push({
      //         fileName: 'Cantidad de archivos',
      //         fileSize: 0,
      //         message: `Máximo 5 archivos permitidos`,
      //       });
      //     }

      //     files.forEach((file) => {
      //       if (file.size > 5 * 1024 * 1024) {
      //         errors.push({
      //           fileName: file.name,
      //           fileSize: file.size,
      //           message: `El archivo excede 5MB`,
      //         });
      //       }

      //       if (file.name.length > 60) {
      //         errors.push({
      //           fileName: file.name,
      //           fileSize: file.size,
      //           message: `El nombre excede 60 caracteres`,
      //         });
      //       }
      //     });

      //     if (errors.length > 0) {
      //       return context.createError({ message: JSON.stringify(errors) });
      //     }
      //     return true;
      //   }
      // ),
    }),
  })

  const minDeclaredValue = (
    weight: number,
    rangeList: RangeDeclareDto[],
    service?: ShipmentService
  ) => {
    let filteredRangeList = rangeList.filter((r) => r.serviceID === service)
    if (filteredRangeList.length === 0) {
      filteredRangeList = rangeList.filter((r) => r.serviceID === 0)
    }

    return (
      filteredRangeList.find(
        (r) => r.minWeight < weight && weight <= r.maxWeight
      )?.minDeclareValue ?? 0
    )
  }

  const [minValue, setMinValue] = useState(0)

  const user = useAppSelector((state) => state.user)!
  const dispatch = useAppDispatch()
  const businessUnitsCatalog = useAppSelector(
    (state) => state.inmutable.businessUnits
  )

  // Cacheado por agreementID: comparte respuesta con el formulario de
  // destinatario, no dispara una segunda consulta.
  const { rows: contractDestinationRows } = useContractDestinationBUs(
    user.client?.agreementID
  )
  const { options: allowedBusinessUnits, restricted: destinationsRestricted } =
    useMemo(
      () =>
        resolveDestinationBusinessUnits(
          businessUnitsCatalog,
          contractDestinationRows,
          new Date()
        ),
      [businessUnitsCatalog, contractDestinationRows]
    )

  const [initialValidationDone, setInitialValidationDone] = useState(false)

  // Validación al montar el componente
  useEffect(() => {
    if (!user || initialValidationDone) return

    // [D8] Sesión vieja (redux-persist) sin la lista: si nunca se buscó
    // (undefined) y hay agreementID, hacer fetch perezoso puntual y esperar a
    // que el store se actualice (el efecto vuelve a correr por depender de user).
    if (user.originBusinessUnits === undefined && !!user.client?.agreementID) {
      getOriginBusinessUnits(user.client.agreementID).then((list) => {
        dispatch(setOriginBusinessUnits(list))
      })
      return
    }

    const missingData = []

    // [D3] Sin oficinas origen usables → bloquear creación de preguía.
    if ((user.originBusinessUnits?.length ?? 0) === 0) {
      missingData.push('oficinas origen disponibles')
    }

    const paymentModeid =
      user.paymentMethod?.[0]?.paymentModeID ?? PaymentModeId.CREDIT
    const accountBillTo = user.paymentMethod?.filter(
      (x) => x.paymentModeID.toString() === paymentModeid.toString()
    )?.[0]?.accountBillTo

    if (
      (accountBillTo === '00000000-0000-0000-0000-000000000000' ||
        accountBillTo === undefined) &&
      paymentModeid.toString() === PaymentModeId.CREDIT.toString()
    ) {
      missingData.push('cuenta para facturar asignada')
    }

    if (missingData.length > 0) {
      setLoadingStatus(ConfirmModalStatus.ERROR)

      if (missingData.length === 2) {
        setLoadingTitle(
          'No tiene cuenta para facturar asignada y no tiene oficinas origen disponibles. ' +
            'Por favor comuníquese con Soporte Técnico.'
        )
      } else {
        setLoadingTitle(
          `Comuníquese con el departamento de Soporte Técnico. ` +
            `No tiene ${missingData[0]}.`
        )
      }

      setLoading(true)
    }

    setInitialValidationDone(true)
  }, [user, initialValidationDone, dispatch])

  let paymentModeid =
    user.paymentMethod != null && user.paymentMethod!?.length > 0
      ? user.paymentMethod![0]?.paymentModeID
      : PaymentModeId.CREDIT

  const preShipmentInitialValues = {
    service: undefined,
    paymentModeID: paymentModeid ?? (PaymentModeId.CREDIT as PaymentModeId),
    shippingMethod: ShippingMethodEnum.LAND,
    isSafeKeeping: paymentModeid?.toString() === PaymentModeId.COD.toString(),
    packageType: PackageTypeEnum.BOX,
    weightUnit: WeightTypeEnum.KG,
    pieces: [] as PieceInterface[],
    tracking: '',
    observations: '',
    valueDeclarate: '',
    items: [] as ItemInterface[],
    deliveryDistance: 0,
    fileUpload: [],
    serviceData: undefined as ServiceDTO | undefined,
    declaratedValueInvoceNum: '',
  }
  const identificationTypes = useAppSelector(
    (state) => state.inmutable.taxIdentificationTypes
  )

  const senderInitialValues = {
    fullName: user.client?.accountFullName ?? '',
    abbreviationName: identificationTypes.find(
      (t) => t.abbreviationName === user.client?.abbreviationName
    )!,
    identificationNumber: user.client?.identificationNumber ?? '',
  }
  const consigneeInitialValues = {
    abbreviationName: identificationTypes.find(
      (t) => t.abbreviationName === 'V-'
    )!,
    id: '',
    identificationNumber: '',
    fullName: '',
    codePhoneNumber: '',
    phoneId: '',
    phoneTypeId: '',
    phoneNumber: '',
    acceptsSMS: false,
    codeCellPhoneNumber: '',
    cellPhoneNumber: '',
    emailId: '',
    email: '',
    city: '',
    cityCode: '',
    postalCode: '',
    address: '',
    reference: '',
    reset: true,
    businessUnit: undefined as BusinessUnitInterface | undefined,
    accountSelected: undefined as AccountInterface | undefined,
    newAccount: undefined,
    ShipmentType: '10' as DeliveryEnum | undefined,
    displayName: '',
    setAccount: false,
  }
  // [D4] Arranca vacío; OriginForm hace auto-set cuando hay 1 sola oficina o
  // cuando hay varias y el usuario tiene una preferencia guardada que sigue
  // siendo oficina origen del contrato.
  const originInitialValues = {
    businessUnit: undefined as BusinessUnitInterface | undefined,
    city: '',
    cityCode: '',
    address: '',
  }
  const [openModal, setOpenModal] = useState(false)
  const [openPrintModal, setOpenPrintModal] = useState(false)
  const [preShipmentModal, setPreShipmentModal] = useState(
    {} as PreShipmentInterface
  )
  const [status, setStatus] = useState(ConfirmModalStatus.CONFIRM)

  let [loading, setLoading] = useState(false)
  let [loadingStatus, setLoadingStatus] = useState(
    ConfirmModalStatus.PROCESSING
  )
  let [loadingTitle, setLoadingTitle] = useState('Cargando...')

  const formInitialValues = {
    sender: senderInitialValues,
    newPiece: newPieceInitialValues,
    consignee: consigneeInitialValues,
    shipment: preShipmentInitialValues,
    origin: originInitialValues,
    shippingLocation: {
      name: '',
      code: '',
      address: '',
      postalCode: '',
      coordinates: {
        lat: 0,
        lng: 0,
      },
    },
  }

  return (
    <MainPage>
      <PageTitle title="Crear preguía" />

      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { resetForm }) => {
          // No basta con que haya origen: el origen sobrevive al resetForm y a
          // la sesión persistida, así que se valida que siga siendo una
          // oficina origen del contrato antes de mandarla como
          // businessUnitOrigin.
          if (
            (user.originBusinessUnits?.length ?? 0) === 0 ||
            values.origin.businessUnit === undefined ||
            !user.originBusinessUnits?.some(
              (bu) => bu.buCode === values.origin.businessUnit?.buCode
            )
          ) {
            setLoadingStatus(ConfirmModalStatus.ERROR)
            setLoadingTitle('Debe seleccionar una oficina origen válida.')
            setLoading(true)
            return
          }

          // Aquí la validación es la ÚNICA que existe: PreShipment/CreateCore
          // no valida el contrato en el backend (0 ocurrencias de
          // AccountSiteBU/allowAnyBU en PreShipmentController.cs). Sin esto un
          // preenvío a una tienda prohibida se guarda bien y falla recién al
          // convertirlo en guía, en manos de otra persona.
          if (
            destinationsRestricted &&
            values.consignee.businessUnit !== undefined &&
            !allowedBusinessUnits.some(
              (bu) => bu.buCode === values.consignee.businessUnit?.buCode
            )
          ) {
            setLoadingStatus(ConfirmModalStatus.ERROR)
            setLoadingTitle(
              'La tienda destino ya no está disponible para tu contrato. Selecciona otra.'
            )
            setLoading(true)
            return
          }

          // const files =
          //   values.shipment.fileUpload !== undefined
          //     ? Array.isArray(values.shipment.fileUpload)
          //       ? values.shipment.fileUpload
          //       : [values.shipment.fileUpload]
          //     : undefined;
          const preShipmentRequest: PreShipmentInterface[] = [
            {
              id: '',
              number: 0,
              customerCode: user.client!.accountCode,
              pieces: values.shipment.pieces,
              observations: values.shipment.observations,
              totalPieces: values.shipment.pieces.length,
              totalWeight: values.shipment.pieces.reduce(
                (acc, cur) => acc + Number(cur.weight),
                0
              ),
              declaratedValue: values.shipment.pieces.reduce(
                (acc, cur) => acc + Number(cur.declaredValue),
                0
              ),
              service: values.shipment.service,
              deliveryType: values.consignee.ShipmentType as DeliveryEnum,
              shippingMethod: values.shipment.shippingMethod,
              creationUser: user.user?.login ?? 'ccw',
              shipper: user.client!,
              businessUnitOrigin: values.origin.businessUnit!,
              businessUnitConsignee: values.consignee.businessUnit!,
              sender: {
                fullName: values.sender.fullName,
                abbreviationId:
                  values.sender.abbreviationName.taxIdentificationTypeId,
                abbreviationName:
                  values.sender.abbreviationName.taxIdentificationTypeCode,
                identificationNumber: values.sender.identificationNumber,
              },
              consignee: {
                id: values.consignee.id,
                accountCode: '',
                agreementId: 0,
                cityCode: values.consignee.cityCode,
                taxIdentificationTypeID:
                  values.consignee.abbreviationName.taxIdentificationTypeId,
                abbreviationName:
                  values.consignee.abbreviationName.taxIdentificationTypeCode,
                identificationNumber: values.consignee.identificationNumber,
                accountFullName: values.consignee.fullName,
                fiscalAddress: values.consignee.address,
                reference: values.consignee.reference,
                listAccountPhone: [
                  {
                    countryId: '236',
                    acceptsSMS: false,
                    countryPhoneAccessCode: '+58',
                    phoneTypeId: '1',
                    phoneNumber:
                      (values.consignee.codePhoneNumber === 'FIJO'
                        ? ''
                        : values.consignee.codePhoneNumber) +
                      '' +
                      values.consignee.phoneNumber,
                  },
                  {
                    countryId: '236',
                    acceptsSMS: values.consignee.acceptsSMS,
                    countryPhoneAccessCode: '+58',
                    phoneTypeId: '2',
                    phoneNumber:
                      (values.consignee.codePhoneNumber === 'FIJO'
                        ? ''
                        : values.consignee.codeCellPhoneNumber) +
                      ' ' +
                      values.consignee.cellPhoneNumber,
                  },
                ],
                listAccountEmail: [
                  {
                    id: '',
                    emailTypeId: '2',
                    email: values.consignee.email,
                  },
                ],
                listAuthorizingAccount: [],
              },
              shippingLocation:
                values.shippingLocation.name !== ''
                  ? values.shippingLocation
                  : {
                      name: values.consignee.city,
                      address: values.consignee.address,
                      postalCode: values.consignee.postalCode,
                      coordinates:
                        values.consignee.businessUnit?.location.coordinates ??
                        ({} as CoordinatesInterface),
                    },
              isSED: false,
              isToHold: false,
              isFragile: false,
              isRepacke: false,
              isPreAlerted: false,
              isSafeKeeping: values.shipment.isSafeKeeping,
              status: ShipmentStatusEnum.ACTIVE,
              packageType: values.shipment.packageType,
              paymentModeID: values.shipment.paymentModeID,
              //files: files,
              declaratedValueInvoceNum:
                values.shipment.declaratedValueInvoceNum,
            },
          ]

          setLoadingStatus(ConfirmModalStatus.PROCESSING)
          setLoadingTitle('Guardando...')
          setLoading(true)

          const newPreShipment = await createPreShipment(preShipmentRequest)

          if (
            newPreShipment.shipmentNumber !== null &&
            newPreShipment.shipmentNumber !== ''
          ) {
            resetForm({
              values: resetValuesKeepingOrigin(formInitialValues, values),
            })
            setPreShipmentModal(newPreShipment)
            setOpenModal(true)
            setStatus(ConfirmModalStatus.SUCCESS)
            setLoading(false)
          } else {
            setLoading(true)
            setLoadingStatus(ConfirmModalStatus.ERROR)
            setLoadingTitle(
              'Existe un error al crear la preguía: ' +
                newPreShipment.errorMessage +
                ' contacte a Soporte Técnico.'
            )
          }
        }}
      >
        {(formik) => (
          <form
            onSubmit={formik.handleSubmit}
            className="flex flex-1 flex-col gap-16 w-full"
          >
            <div className="flex flex-1 flex-col xl:flex-row gap-16">
              {/* Shipper */}
              <Card title="Datos del Remitente">
                <div className="flex h-full flex-col gap-1 mt-1 py-4 mb-6 px-4 sm:px-12">
                  {/* Detalles del Remitente */}
                  <h4 className="text-sm text-main-500 gap-2 font-bold mb-3 mt-1">
                    Detalles del Remitente:
                  </h4>
                  <SenderForm
                    formik={formik}
                    wrapperClassname="flex flex-col gap-1"
                    senderPhone={
                      user.client?.listAccountPhone?.[0]?.phoneNumber ?? ''
                    }
                  />

                  {/* Detalle del Origen */}
                  <hr className="my-3" />
                  <h4 className="text-sm text-main-500 gap-2 font-bold mb-3 mt-1">
                    Detalle del Origen:
                  </h4>
                  <OriginForm formik={formik} />
                </div>
              </Card>

              {/* Consignee */}
              <Card title="Datos del Destinatario">
                <div className="flex flex-1 flex-col py-4 mb-6 px-4 sm:px-12">
                  <ConsigneeFormV2 formik={formik} />
                </div>
              </Card>
            </div>
            {formik.values.consignee.id !== '' && (
              <div className="flex flex-1 max-w-full">
                {/* Shipment */}
                <Card title="Datos del Envío" className="max-w-full">
                  <div className="flex flex-1 flex-col px-4 sm:px-12 py-4 mb-6">
                    <PreShipmentFormV2 formik={formik} />
                  </div>
                </Card>
              </div>
            )}
            {(preShipmentModal?.shipmentNumber?.length ?? 0) > 0 && (
              <PresipmentMessageModal
                open={openModal}
                status={status}
                shipment={preShipmentModal}
                setOpen={setOpenModal}
                onPrint={() => {
                  setOpenPrintModal(true)
                  setOpenModal(false)
                }}
              />
            )}

            {(preShipmentModal?.shipmentNumber?.length ?? 0) > 0 &&
              openPrintModal && (
                <PresipmentImpresionModal
                  open={openPrintModal}
                  shipment={preShipmentModal}
                  setOpen={setOpenPrintModal}
                  onPrint={() => {}}
                />
              )}

            <ModalLoading
              open={loading}
              title={loadingTitle}
              status={loadingStatus}
              setOpen={setLoading}
              onPrint={() => {}}
            />

            {formik.isSubmitting && (
              <ScrollToError
                formikCount={Object.keys(formik.errors).length > 0}
                data={formik.errors}
              />
            )}
          </form>
        )}
      </Formik>
    </MainPage>
  )
}

export default CreatePreShipment
